/**
 * QuickReplies — Kontextabhängige Antwort-Vorschläge für AYU
 * Zeigt nach jeder AYU-Antwort 2-3 klickbare Vorschläge an.
 */
import { useMemo } from "react";
import { MessageCircle, ArrowRight, Sparkles } from "lucide-react";

interface QuickRepliesProps {
  lastAssistantMessage?: string;
  chatType: "ayu" | "tagebuch";
  onSelect: (text: string) => void;
  disabled?: boolean;
  className?: string;
}

const GENERIC_COACH = [
  "Erzähl mir mehr darüber",
  "Was kann ich konkret tun?",
  "Ich möchte das Thema wechseln",
  "Kannst du mir ein Beispiel geben?",
  "Wie passt das zu meinem Phänotyp?",
  "Was würdest du mir empfehlen?",
  "Ich brauche einen anderen Blickwinkel",
  "Das trifft es genau — erzähl weiter",
];

const GENERIC_TAGEBUCH = [
  "Ich möchte das Coaching dazu",
  "Was fällt dir an meinem Eintrag auf?",
  "Gibt es ein Muster in meinen Einträgen?",
  "Wie kann ich morgen besser starten?",
  "Was sagt mein Körper dazu?",
];

function detectContext(text: string): string[] {
  const suggestions: string[] = [];
  const lower = text.toLowerCase();

  if (
    lower.includes("traurig") ||
    lower.includes("trauer") ||
    lower.includes("weinen")
  ) {
    suggestions.push("Was hilft mir gerade am meisten?");
    suggestions.push("Woher kommt dieses Gefühl?");
  }
  if (
    lower.includes("angst") ||
    lower.includes("sorge") ||
    lower.includes("panik")
  ) {
    suggestions.push("Gibt es eine Atemübung für mich?");
    suggestions.push("Was kann ich jetzt sofort tun?");
  }
  if (
    lower.includes("schlaf") ||
    lower.includes("müde") ||
    lower.includes("erschöpf")
  ) {
    suggestions.push("Was kann ich für besseren Schlaf tun?");
    suggestions.push("Welche ayurvedische Abendroutine passt zu mir?");
  }
  if (
    lower.includes("verdauung") ||
    lower.includes("darm") ||
    lower.includes("ernährung") ||
    lower.includes("essen")
  ) {
    suggestions.push("Welche Lebensmittel passen zu meinem Phänotyp?");
    suggestions.push("Wie kann ich mein Agni stärken?");
  }
  if (
    lower.includes("stress") ||
    lower.includes("druck") ||
    lower.includes("arbeit")
  ) {
    suggestions.push("Wie setze ich Grenzen?");
    suggestions.push("Welche Pranayama-Übung hilft bei Stress?");
  }
  if (
    lower.includes("gut") ||
    lower.includes("besser") ||
    lower.includes("freude") ||
    lower.includes("dankbar")
  ) {
    suggestions.push("Wie kann ich dieses Gefühl festhalten?");
    suggestions.push("Was hat dazu beigetragen?");
  }
  if (
    lower.includes("phänotyp") ||
    lower.includes("cluster") ||
    lower.includes("typ") ||
    lower.includes("ayu-typ") ||
    lower.includes("menschentyp")
  ) {
    suggestions.push("Was bedeutet mein AYU-Phänotyp genau?");
    suggestions.push("Welche Routine passt zu meinem Typ?");
  }
  if (
    lower.includes("übung") ||
    lower.includes("yoga") ||
    lower.includes("meditation")
  ) {
    suggestions.push("Zeig mir eine konkrete Übung");
    suggestions.push("Wie oft sollte ich das machen?");
  }

  return suggestions;
}

export default function QuickReplies({
  lastAssistantMessage,
  chatType,
  onSelect,
  disabled = false,
  className = "",
}: QuickRepliesProps) {
  const suggestions = useMemo(() => {
    const result: string[] = [];

    if (lastAssistantMessage) {
      const contextSuggestions = detectContext(lastAssistantMessage);
      result.push(...contextSuggestions.slice(0, 2));
    }

    const generics = chatType === "ayu" ? GENERIC_COACH : GENERIC_TAGEBUCH;
    const shuffled = [...generics].sort(() => Math.random() - 0.5);

    for (const s of shuffled) {
      if (result.length >= 3) break;
      if (!result.includes(s)) result.push(s);
    }

    return result.slice(0, 3);
  }, [lastAssistantMessage, chatType]);

  if (suggestions.length === 0 || disabled) return null;

  return (
    <div className={`mt-4 flex flex-wrap gap-2.5 ${className}`}>
      {suggestions.map((suggestion, i) => (
        <button
          key={`${suggestion}-${i}`}
          onClick={() => onSelect(suggestion)}
          className="
            group flex items-center gap-2 
            px-4 py-2 rounded-full
            text-xs font-medium
            bg-[#6b7f4e]/5 text-[#6b7f4e]/70
            border border-[#d4dcc4]
            hover:bg-[#6b7f4e]/10 hover:text-[#6b7f4e] hover:border-[#d4dcc4]
            transition-all duration-200
            animate-in fade-in slide-in-from-bottom-2
          "
          style={{ animationDelay: `${i * 100}ms`, animationFillMode: "both" }}
        >
          {i === 0 ? (
            <Sparkles className="w-3 h-3 opacity-50 group-hover:opacity-100 transition-opacity" />
          ) : i === 1 ? (
            <MessageCircle className="w-3 h-3 opacity-50 group-hover:opacity-100 transition-opacity" />
          ) : (
            <ArrowRight className="w-3 h-3 opacity-50 group-hover:opacity-100 transition-opacity" />
          )}
          {suggestion}
        </button>
      ))}
    </div>
  );
}
